import { Badge, Button, Card, cn, Input } from '@dnx/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { OrgAvatar } from './org-avatar.js';
import { fmtDate, orgsQuery, planLabel, STATUS_LABEL } from './queries.js';

const FILTERS = [
  { value: 'all', label: 'Todas' },
  { value: 'active', label: 'Ativas' },
  { value: 'onboarding', label: 'Implantação' },
  { value: 'suspended', label: 'Suspensas' },
] as const;

export function OrganizationsListPage() {
  const { data: orgs } = useSuspenseQuery(orgsQuery);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<(typeof FILTERS)[number]['value']>('all');

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return orgs.filter((o) => (status === 'all' || o.status === status) && (!term
      || `${o.tradeName} ${o.legalName} ${o.slug} ${o.cnpj}`.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(term)));
  }, [orgs, q, status]);

  const totals = { clinics: orgs.length, members: orgs.reduce((a, o) => a + o.membersCount, 0), contacts: orgs.reduce((a, o) => a + o.contactsCount, 0) };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">Clínicas</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {totals.clinics} clínicas · {totals.members} pessoas nas equipes · {totals.contacts} pacientes
          </p>
        </div>
        <Link to="/clinicas/nova"><Button><Plus className="size-4" />Nova clínica</Button></Link>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle" aria-hidden />
          <Input aria-label="Buscar clínica" placeholder="Buscar por nome, endereço ou CNPJ" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-1.5" role="group" aria-label="Filtrar por status">
          {FILTERS.map((f) => (
            <button key={f.value} type="button" aria-pressed={status === f.value} onClick={() => setStatus(f.value)}
              className={cn('h-8 rounded-full border px-3 text-[13px] font-medium transition-colors',
                status === f.value ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-fg-muted hover:text-fg')}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="mt-4 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-surface-muted/60 text-left text-[12px] font-medium text-fg-muted">
              <tr>
                <th className="px-4 py-2.5 font-medium">Clínica</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 text-right font-medium">Equipe</th>
                <th className="px-4 py-2.5 text-right font-medium">Pacientes</th>
                <th className="px-4 py-2.5 font-medium">Plano</th>
                <th className="px-4 py-2.5 font-medium">Criada em</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((o) => {
                const s = STATUS_LABEL[o.status]!;
                return (
                  <tr key={o.id} className="group cursor-pointer hover:bg-surface-muted/50">
                    <td className="px-4 py-3">
                      <Link to="/clinicas/$id" params={{ id: o.id }} className="flex items-center gap-3">
                        <OrgAvatar name={o.tradeName} color={o.primaryColor} />
                        <span className="min-w-0">
                          <span className="block font-medium text-fg group-hover:text-primary">{o.tradeName}</span>
                          <span className="block font-mono text-[11.5px] text-fg-muted">{o.portalHost}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3"><Badge tone={s.tone}>{s.label}</Badge></td>
                    <td className="px-4 py-3 text-right tabular-nums text-fg">{o.membersCount}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-fg">{o.contactsCount}</td>
                    <td className="px-4 py-3 text-fg-muted">{planLabel(o.plan)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-fg-muted">{fmtDate(o.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="px-4 py-12 text-center text-sm text-fg-muted">
            {orgs.length === 0 ? 'Nenhuma clínica ainda. Cadastre a primeira.' : 'Nenhuma clínica com esses filtros.'}
          </p>
        )}
      </Card>
    </div>
  );
}
